import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import UndoIcon from '@mui/icons-material/Undo';
import SearchIcon from '@mui/icons-material/Search';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box, Divider, Grid, Typography, IconButton, Chip, TextField, InputAdornment, Tooltip as MuiTooltip } from '@mui/material';
import { Status, Wrapper } from "@googlemaps/react-wrapper";
import { GestaoClickStation } from './GestaoClickStation';
import React, { Fragment, useState, useRef, useEffect, useCallback } from 'react';
import {
  ArrayInput,
  AutocompleteInput,
  BooleanInput,
  Button,
  DateInput,
  Edit,
  FileField,
  FileInput,
  FormDataConsumer,
  FormTab,
  ImageField,
  ImageInput,
  Labeled,
  LoadingIndicator,
  NumberInput,
  PasswordInput,
  ReferenceArrayInput,
  ReferenceInput,
  SaveButton,
  SelectArrayInput,
  SelectInput,
  SimpleFormIterator,
  TabbedForm,
  TextInput,
  TimeInput,
  Toolbar,
  email,
  maxLength,
  maxValue,
  minValue,
  useDataProvider,
  useNotify,
  usePermissions,
  useRecordContext,
  useRedirect,
  useRefresh,
  useResourceContext,
  useTranslate
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import { CheckChainUsesModule, ModuleKey, Permission, Permissions, PriceInput, Tooltip } from '../../components';
import { EntityChangeLogTab } from '../change-log/EntityChangeLogTab';
import { CEPInput, CNPJInput, PhoneInput, RtiTokenInput, StateInput } from '../../components/Inputs';
import { CnpjLookupButton } from './CnpjLookupButton';
import { EvoCnpjCheck, validateEvoCnpjCheck } from './EvoCnpjCheck';
import { CommissioningType, commissioningTypeChoices, gestaoClickStoreChoices } from '../../models';
import { MAX_DATE, PERMISSIONS, daysOfTheWeek, minimumValueCashbackToolTip } from '../../constants';
import { ConcentratorBrandType, FinancePaymentMethodType, IntegrationType, PaymentGatewayType, TagType } from '../../models';
import { SislavFields, clearSislavFields } from '../../components/SislavFields';
import { filterToQuery } from '../../utils';
import { filterNumericIds } from '../../lib/stationPartnerForm';

// Componente de mapa interativo para edição de coordenadas
const CoordinatesMapEditor = React.memo(({ coordinates = [], onChange, maxPoints = 4 }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const polygonRef = useRef(null);
  const markersRef = useRef([]);
  const pendingMarkerRef = useRef(null);
  const coordinatesRef = useRef(coordinates);
  const geocoderRef = useRef(null);
  const [pendingPoint, setPendingPoint] = useState(null);
  const [searchAddress, setSearchAddress] = useState('');
  const [searching, setSearching] = useState(false);

  // Manter ref atualizada
  useEffect(() => {
    coordinatesRef.current = coordinates;
  }, [coordinates]);

  // Inicializar mapa
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    // Centro padrão (Brasil) ou primeiro ponto se existir
    const initialCoords = coordinatesRef.current;
    const defaultCenter = initialCoords.length > 0
      ? { lat: initialCoords[0].latitude, lng: initialCoords[0].longitude }
      : { lat: -15.792487, lng: -47.893526 };

    const mapInstance = new google.maps.Map(mapRef.current, {
      center: defaultCenter,
      zoom: initialCoords.length > 0 ? 18 : 4,
      mapId: import.meta.env.VITE_MAP_ID || 'd06eb9fab6b219cd',
    });
    mapInstanceRef.current = mapInstance;

    // Inicializar Geocoder
    geocoderRef.current = new google.maps.Geocoder();

    // Click listener para adicionar pontos (usa ref para ter valor atualizado)
    mapInstance.addListener('click', (e) => {
      if (coordinatesRef.current.length >= maxPoints) return;

      const newPoint = {
        latitude: e.latLng.lat(),
        longitude: e.latLng.lng()
      };
      setPendingPoint(newPoint);
    });

    // Ajustar bounds se tiver coordenadas
    if (initialCoords.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      initialCoords.forEach(coord => {
        bounds.extend({ lat: coord.latitude, lng: coord.longitude });
      });
      mapInstance.fitBounds(bounds, 50);
    }
  }, [maxPoints]);

  // Atualizar marcador pendente
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    // Remover marcador pendente anterior
    if (pendingMarkerRef.current) {
      pendingMarkerRef.current.map = null;
      pendingMarkerRef.current = null;
    }

    if (pendingPoint) {
      const markerContent = document.createElement('div');
      markerContent.style.cssText = `
        width: 30px;
        height: 30px;
        border-radius: 50%;
        background-color: #ff9800;
        border: 3px dashed white;
        box-shadow: 0 2px 6px rgba(0,0,0,0.4);
        animation: pulse 1s infinite;
      `;

      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: { lat: pendingPoint.latitude, lng: pendingPoint.longitude },
        map,
        content: markerContent,
      });
      pendingMarkerRef.current = marker;
    }
  }, [pendingPoint]);

  // Atualizar polígono e marcadores confirmados
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    // Remover marcadores anteriores
    markersRef.current.forEach(m => { m.map = null; });
    markersRef.current = [];

    // Remover polígono anterior
    if (polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
    }

    if (coordinates.length === 0) return;

    const paths = coordinates.map(coord => ({
      lat: coord.latitude,
      lng: coord.longitude
    }));

    // Criar marcadores
    paths.forEach((path, index) => {
      const markerContent = document.createElement('div');
      markerContent.style.cssText = `
        width: 28px;
        height: 28px;
        border-radius: 50%;
        background-color: #1976d2;
        border: 2px solid white;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: bold;
        font-size: 12px;
        cursor: pointer;
      `;
      markerContent.textContent = String(index + 1);

      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: path,
        map,
        content: markerContent,
        title: `Ponto ${index + 1} - Clique para remover`,
      });

      marker.addListener('click', () => {
        const newCoords = coordinates.filter((_, i) => i !== index);
        onChange(newCoords);
      });

      markersRef.current.push(marker);
    });

    // Criar polígono se tiver 3+ pontos
    if (coordinates.length >= 3) {
      const polygon = new google.maps.Polygon({
        paths,
        strokeColor: '#666666',
        strokeOpacity: 0.9,
        strokeWeight: 2,
        fillColor: '#999999',
        fillOpacity: 0.35,
      });
      polygon.setMap(map);
      polygonRef.current = polygon;
    }
  }, [coordinates, onChange]);

  const handleConfirm = useCallback(() => {
    if (!pendingPoint) return;
    const newCoords = [...coordinates, pendingPoint];
    onChange(newCoords);
    setPendingPoint(null);
  }, [pendingPoint, coordinates, onChange]);

  const handleCancel = useCallback(() => {
    setPendingPoint(null);
  }, []);

  const handleClearAll = useCallback(() => {
    onChange([]);
    setPendingPoint(null);
  }, [onChange]);

  const handleSearch = useCallback(() => {
    if (!searchAddress.trim() || !geocoderRef.current || !mapInstanceRef.current) return;

    setSearching(true);
    geocoderRef.current.geocode({ address: searchAddress }, (results, status) => {
      setSearching(false);
      if (status === 'OK' && results[0]) {
        const location = results[0].geometry.location;
        mapInstanceRef.current.setCenter(location);
        mapInstanceRef.current.setZoom(18);
      }
    });
  }, [searchAddress]);

  const handleSearchKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  }, [handleSearch]);

  return (
    <Box sx={{ width: '100%', maxWidth: 800 }}>
      {/* Campo de busca de endereço */}
      <Box sx={{ mb: 2 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Buscar endereço (ex: Rua das Flores, 123, São Paulo)"
          value={searchAddress}
          onChange={(e) => setSearchAddress(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          disabled={searching}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleSearch} disabled={searching || !searchAddress.trim()} size="small">
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="body2" color="textSecondary">
          Clique no mapa para adicionar pontos ({coordinates.length}/{maxPoints})
        </Typography>
        {coordinates.length > 0 && (
          <IconButton size="small" onClick={handleClearAll} title="Limpar todos">
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <div ref={mapRef} style={{ height: 400, minHeight: 400, width: '100%', minWidth: 300, borderRadius: 8, border: '1px solid #ddd' }} />

      {pendingPoint && (
        <Box sx={{ mt: 1, p: 1, bgcolor: '#fff3e0', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={`Lat: ${pendingPoint.latitude.toFixed(6)}, Lng: ${pendingPoint.longitude.toFixed(6)}`}
            size="small"
            color="warning"
          />
          <IconButton size="small" color="success" onClick={handleConfirm} title="Confirmar ponto">
            <CheckIcon />
          </IconButton>
          <IconButton size="small" color="error" onClick={handleCancel} title="Cancelar">
            <UndoIcon />
          </IconButton>
        </Box>
      )}

      {coordinates.length > 0 && (
        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {coordinates.map((coord, index) => (
            <Chip
              key={index}
              label={`${index + 1}: ${coord.latitude.toFixed(5)}, ${coord.longitude.toFixed(5)}`}
              size="small"
              onDelete={() => {
                const newCoords = coordinates.filter((_, i) => i !== index);
                onChange(newCoords);
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
});

const CoordinatesMapInput = () => {
  const form = useFormContext();
  const coordinates = form.watch('coordinates') || [];

  const handleChange = useCallback((newCoords) => {
    form.setValue('coordinates', newCoords, { shouldDirty: true });
  }, [form]);

  return (
    <Wrapper
      apiKey={import.meta.env.VITE_MAP_API_KEY || 'AIzaSyA2-gtjUXGbaFwbFy8pmjTPgoV4pTLbx2g'}
      libraries={['marker']}
      render={(status) => {
        if (status === Status.LOADING) return <Typography>Carregando mapa...</Typography>;
        if (status === Status.FAILURE) return <Typography color="error">Erro ao carregar mapa</Typography>;
        return null;
      }}
    >
      <CoordinatesMapEditor coordinates={coordinates} onChange={handleChange} maxPoints={4} />
    </Wrapper>
  );
};

const choices = [
  { id: 'company-tec', name: 'CompanyTec' },
  { id: 'ez-tec', name: 'EZTec' },
  { id: 'auto-system', name: 'AutoSystem' },
];

const required = (message = 'Required') => value =>
  value ? undefined : message;

export const FinancesStation = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const form = useFormContext();
  const retailerId = useWatch({ name: 'retailerId' });
  const commissioningType = useWatch({ name: 'commissioningType' });

  const handleChange = (retailerId) => {
    dataProvider.getOne("retailers", { id: retailerId })
      .then(({ data: retailer }) => {
        form.setValue('commissioningType', retailer.commissioningType);
        form.setValue('commissioningValue', retailer.commissioningValue);
      })
      .catch((err) => {
        notify('Não foi possível realizar a operação, tente novamente', { type: 'warning' });
      });
  };

  return (
    <>
      <Grid container spacing={2} sx={{ maxWidth: 800 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <PriceInput source="monthlyPrice" label="Valor da mensalidade" fullWidth />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <DateInput source="billingStartDate" label="Data de início do faturamento" inputProps={{ max: MAX_DATE }} fullWidth />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <DateInput source="installationStartDate" label="Data de início da implantação" inputProps={{ max: MAX_DATE }} fullWidth helperText="Marco inicial para cálculo do tempo de implantação. Deixe vazio para usar a data de criação do posto." />
        </Grid>
      </Grid>
      {/* Os itens sao filhos diretos do container de proposito: com FormDataConsumer no meio, o
          Grid deixava de ser item e o spacing nao se aplicava — os tres campos ficavam colados. */}
      <Grid container spacing={2} sx={{ maxWidth: 800 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <ReferenceInput
            source="retailerId"
            reference="retailers"
            perPage={null}
            sort={{ field: 'name', order: 'ASC' }}
          >
            <SelectInput
              label="Revenda"
              optionText="name"
              onChange={e => handleChange(e.target.value)}
              validate={[required()]}
              fullWidth
            />
          </ReferenceInput>
        </Grid>
        {retailerId && (
          <Grid size={{ xs: 12, md: 4 }}>
            <SelectInput
              source="commissioningType"
              label="Tipo de comissão"
              choices={commissioningTypeChoices}
              validate={[required()]}
              fullWidth
            />
          </Grid>
        )}
        {retailerId && commissioningType === CommissioningType.PERCENTAGE && (
          <Grid size={{ xs: 12, md: 4 }}>
            <NumberInput
              source="commissioningValue"
              label="Valor da comissão"
              min={0.01}
              max={100}
              step={0.01}
              validate={[minValue(0.01), maxValue(100), required()]}
              placeholder="%"
              helperText="Percentual sobre a mensalidade"
              fullWidth
            />
          </Grid>
        )}
        {retailerId && commissioningType === CommissioningType.FIXED && (
          <Grid size={{ xs: 12, md: 4 }}>
            <PriceInput
              source="commissioningValue"
              label="Valor da comissão"
              validate={[minValue(0.01), required()]}
              helperText="Valor fixo por mês"
              fullWidth
            />
          </Grid>
        )}
      </Grid>
      <Grid container spacing={2} sx={{ maxWidth: 800 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextInput source="emailInvoice" label="E-mail para envio da nota fiscal" validate={email()} fullWidth />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextInput source="emailBill" label="E-mail para envio do boleto" validate={email()} fullWidth />
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ maxWidth: 800 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <PriceInput
            source="installationCost"
            label="Valor da instalação"
            validate={[minValue(0.01)]}
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <NumberInput source="monthlyDueDate" label="Vencimento da mensalidade (dia)" validate={[minValue(1), maxValue(31)]} fullWidth />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <SelectInput
            source="gestaoClickStoreId"
            label="Loja do Gestão Click"
            choices={gestaoClickStoreChoices}
            emptyText="Não definida"
            helperText="Define a conta bancária e a forma de pagamento do boleto"
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <NumberInput source="gracePeriod" label="Período de carência (mês)" validate={[minValue(1)]} fullWidth />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextInput source="graceReason" label="Motivo da carência" fullWidth />
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ maxWidth: 800 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <SelectInput source="financePaymentMethod" label="Forma de pagamento" choices={FinancePaymentMethodType.financePaymentMethodChoices} fullWidth />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextInput source="responsible" label="Responsável" fullWidth />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <PhoneInput source="contactResponsible" label="Contato" fullWidth />
        </Grid>
      </Grid>
    </>
  );
};

export const EditStation = () => {
  const { permissions } = usePermissions();
  const translate = useTranslate();

  const StationEditToolbar = props => {
    const [updating, setUpdating] = useState(false);
    const notify = useNotify();
    const redirect = useRedirect();
    const refresh = useRefresh();

    const dataProvider = useDataProvider();
    const record = useRecordContext();
    if (!record) { return null; }
    const resource = useResourceContext();

    const handleClick = () => {
      setUpdating(true);
      dataProvider.update(resource, {
        id: record.id,
        data: { isActive: !record.isActive },
        previousData: { ...record }
      })
        .then(() => {
          notify(`Posto ${record.isActive ? "desativado" : "ativado"} com sucesso`);
        }).catch(() => {
          notify(`Erro ao ${record.isActive ? "desativar" : "ativar"} posto`, { type: 'warning' });
        }).finally(() => {
          setUpdating(false);
          refresh();
        });
    };

    return (
      <Toolbar style={{ display: 'flex', justifyContent: 'space-between' }}>
        <SaveButton />
        <Permission permission={PERMISSIONS.ADMIN}>
          <Button
            startIcon={updating ? <LoadingIndicator /> : record?.isActive ? <RemoveCircleIcon /> : <AddCircleIcon />}
            label={record?.isActive ? 'Desativar' : 'Ativar'}
            onClick={handleClick}
            disabled={updating}
          />
        </Permission>
      </Toolbar>
    );
  };

  const transform = (data) => {
    if (data.concentrators) {
      data.concentrators = data.concentrators.map(concentrator => ({ numericId: concentrator.numericId, brandType: concentrator.brandType }));
    }
    if (data.integration !== IntegrationType.rti) {
      data.rtiToken = null;
    }
    if (data.integration !== IntegrationType.trevo) {
      data.trevoToken = null;
    }
    clearSislavFields(data);
    // Estado da validação do CNPJ no EVO: vive só na tela. O patchStationModel (Joi) não
    // aceita chave desconhecida e devolveria "Invalid request payload input".
    delete data.evoCheckStatus;
    // Linha vazia de "IDs Numéricos" vira null e o Joi recusa; [] limpa os ids no controller.
    return filterNumericIds(data);
  }

  return (
    <Edit title="Editar posto" transform={transform}>
      <TabbedForm toolbar={<StationEditToolbar />} sanitizeEmptyValues>
        {Permissions({
          permissions: [PERMISSIONS.REGISTER_STATION_IDENTIFICATION, PERMISSIONS.UPDATE_STATION],
          children: (
            <FormTab label="Identificação">
              <TextInput disabled label="ID" source="id" />
              <Permission permission={PERMISSIONS.REGISTER_STATION_IDENTIFICATION}>
                <ArrayInput source="numericIds" label="IDs Numéricos">
                  <SimpleFormIterator
                    disableReordering
                    getItemLabel={() => null}

                  >
                    <NumberInput label={''} />
                  </SimpleFormIterator>
                </ArrayInput>
              </Permission>
              <Grid container spacing={2} sx={{ maxWidth: 800 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextInput source="name" label="Nome" validate={required()} fullWidth />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <CNPJInput source="cnpj" label="CNPJ" validate={required()} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <CnpjLookupButton />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextInput
                    source="email"
                    label="Email"
                    type="email"
                    validate={email()}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <PhoneInput source="phone" label="Telefone" fullWidth />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextInput source="corporateName" label="Razão Social" validate={[maxLength(120)]} fullWidth />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextInput source="stateRegistration" label="Inscrição Estadual" validate={[maxLength(20)]} fullWidth />
                </Grid>
                {permissions && permissions.includes(PERMISSIONS.REGISTER_STATION_IDENTIFICATION) && (
                  <Grid size={{ xs: 12, md: 10 }}>
                    <ReferenceInput
                      source="chainId"
                      reference="chains"
                      perPage={null}
                      sort={{ field: 'name', order: 'ASC' }}
                    >
                      <AutocompleteInput
                        label="Rede"
                        optionText="name"
                        filterToQuery={filterToQuery}
                        validate={required()}
                        fullWidth
                      />
                    </ReferenceInput>
                  </Grid>
                )}
                <Grid size={{ xs: 12, md: 5 }}>
                  <ReferenceArrayInput
                    sort={{ field: 'name', order: 'ASC' }}
                    perPage={null}
                    source="fuelIds"
                    reference="fuels"
                  >
                    <SelectArrayInput label="Combustível" optionText="name" fullWidth />
                  </ReferenceArrayInput>
                </Grid>
                <Grid size={{ xs: 12, md: 5 }}>
                  <ReferenceArrayInput
                    sort={{ field: 'name', order: 'ASC' }}
                    perPage={null}
                    source="serviceIds"
                    reference="services"
                  >
                    <SelectArrayInput label="Serviços" optionText="name" fullWidth />
                  </ReferenceArrayInput>
                </Grid>
                <Grid size={{ xs: 12, md: 5 }}>
                  <FormDataConsumer>
                    {({ formData }) => (
                      <ReferenceArrayInput
                        sort={{ field: 'name', order: 'ASC' }}
                        filter={{ type: TagType.station, chainId: formData.chainId }}
                        perPage={null}
                        source="tagIds"
                        reference="chains/chainId/tag"
                      >
                        <SelectArrayInput label="Etiquetas" optionText="name" fullWidth />
                      </ReferenceArrayInput>
                    )}
                  </FormDataConsumer>
                </Grid>
                <Permission permission={PERMISSIONS.REGISTER_STATION_IDENTIFICATION}>
                  {/* `size={12}`: container aninhado sem size não ocupa a linha inteira no Grid v2,
                      e o `md: 6` dos filhos saía de uma largura menor que a das outras linhas. */}
                  <Grid container size={12} spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <SelectInput
                        label="Integração"
                        emptyText="ra.action.clear_input_value"
                        source="integration"
                        choices={IntegrationType.integrationChoices}
                        validate={validateEvoCnpjCheck}
                        fullWidth
                      />
                    </Grid>
                    <EvoCnpjCheck />
                    <FormDataConsumer>
                      {({ formData }) => (
                        formData.integration === IntegrationType.rti &&
                        <Grid size={{ xs: 12, md: 4 }}>
                          <RtiTokenInput
                            label="Token RTI"
                            source="rtiToken"
                            isRequired
                            fullWidth
                          />
                        </Grid>
                      )}
                    </FormDataConsumer>
                    <FormDataConsumer>
                      {({ formData }) => (
                        formData.integration === IntegrationType.trevo &&
                        <Grid size={{ xs: 12, md: 4 }}>
                          <TextInput
                            label="Token Top Banho (Rede Trevo)"
                            source="trevoToken"
                            fullWidth
                          />
                        </Grid>
                      )}
                    </FormDataConsumer>
                    <SislavFields />
                  </Grid>
                </Permission>
              </Grid>
              <ImageInput source="imagefile" label="Imagem" accept={{ 'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'] }}>
                <ImageField source="src" label="Imagem" />
              </ImageInput>
            </FormTab>
          )
        })}
        {Permissions({
          permissions: [PERMISSIONS.REGISTER_STATION_IDENTIFICATION, PERMISSIONS.UPDATE_STATION],
          children: (
            <FormTab label="Configurações">
              {/* ── Abastecimento ── */}
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>Abastecimento</Typography>
              <Divider sx={{ mb: 2, maxWidth: 600 }} />
              <Permission permission={PERMISSIONS.REGISTER_STATION_IDENTIFICATION}>
                <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.presetUsingValue')}>
                  <BooleanInput label="Preset por Valor (Encher tanque)" source="presetUsingValue" />
                </Tooltip>
              </Permission>
              <BooleanInput label="Usar integração com Software House" source="useIntegrationSoftwareHouse" />
              <BooleanInput label="Acompanhar Abastecimento GNV em tempo real" source="useFillinFollowUpGnv" />
              <BooleanInput label="Acompanhar Abastecimento em tempo real (Outros combustíveis)" source="useFillinFollowUp" />
              <BooleanInput label="Mostrar preço durante o abastecimento" source="appShowPricePerLiterOnFillin" />

              {/* ── Níveis de Preço ── */}
              <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 2, mb: 0.5 }}>Níveis de Preço</Typography>
              <Divider sx={{ mb: 2, maxWidth: 600 }} />
              <BooleanInput label="Usar níveis de preço" source="usePriceTiers" />
              <FormDataConsumer>
                {({ formData }) => formData.usePriceTiers && (
                  <ArrayInput source="priceTierLabels" label="Rótulos dos níveis de preço" helperText="Ex: índice 1 = Dinheiro, índice 2 = Crédito">
                    <SimpleFormIterator disableReordering inline>
                      <NumberInput source="index" label="Índice (1, 2, 3...)" min={1} style={{ width: 160 }} />
                      <TextInput source="label" label="Rótulo" style={{ width: 180 }} />
                      <TextInput
                        source="icon"
                        label="Ícone"
                        style={{ width: 200 }}
                        slotProps={{ input: { endAdornment: (
                          <InputAdornment position="end">
                            <MuiTooltip title="Nome do ícone da biblioteca MaterialCommunityIcons. Acesse materialdesignicons.com para pesquisar. Ex: credit-card, cash, bank" placement="top">
                              <HelpOutlineIcon fontSize="small" color="action" sx={{ cursor: 'help' }} />
                            </MuiTooltip>
                          </InputAdornment>
                        ) } }}
                      />
                      <BooleanInput source="applyDiscount" label="Aplicar desconto" defaultValue={true} helperText="Desative para não aplicar descontos neste nível de preço" />
                    </SimpleFormIterator>
                  </ArrayInput>
                )}
              </FormDataConsumer>

              {/* ── Cashback e Pontuação ── */}
              <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 2, mb: 0.5 }}>Cashback e Pontuação</Typography>
              <Divider sx={{ mb: 2, maxWidth: 600 }} />
              <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.generateCashbackOnFillin')}>
                <BooleanInput label="Gerar cashback no abastecimento" source="generateCashbackOnFillin" defaultValue />
              </Tooltip>
              <BooleanInput label="Aceita cashback?" source="acceptsCashback" defaultValue={false} />
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 5 }}>
                  <PriceInput
                    source="minimumValueToRedeemCashback"
                    label="Mínimo para resgate de cashback"
                    fullWidth
                    slotProps={{ input: { endAdornment: (
                      <InputAdornment position="end">
                        <MuiTooltip title={minimumValueCashbackToolTip} placement="top">
                          <HelpOutlineIcon fontSize="small" color="action" sx={{ cursor: 'help' }} />
                        </MuiTooltip>
                      </InputAdornment>
                    ) } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 5 }}>
                  <NumberInput source="couponRedemptionDaysLimit" label="Limite de dias para resgate de cupom" min={1} fullWidth />
                </Grid>
              </Grid>
              <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.pointsWhenDiscounting')}>
                <BooleanInput label="Pontuar quando usar desconto" source="pointsWhenDiscounting" />
              </Tooltip>
            </FormTab>
          )
        })}
        {
          Permissions({
            permissions: [PERMISSIONS.REGISTER_STATION_CONCENTRATOR],
            children: (
              <FormTab label="Concentradores">
                <ArrayInput source="concentrators" label="IDs Numéricos">
                  <SimpleFormIterator
                    disableReordering
                    getItemLabel={() => null}
                  >
                    <NumberInput source="numericId" label="Id numérico" style={{ width: '260px' }} />
                    <SelectInput
                      validate={[required()]}
                      label="Sistema"
                      source="brandType"
                      choices={ConcentratorBrandType.concentratorBrandChoices}
                      style={{ width: '260px' }}
                    />
                  </SimpleFormIterator>
                </ArrayInput>
              </FormTab>
            )
          })
        }
        {Permissions({
          permissions: [PERMISSIONS.REGISTER_STATION_ADDRESS],
          children: (
            <FormTab label="Endereço">
              <Grid container spacing={2} sx={{ maxWidth: 800 }}>
                <Grid size={{ xs: 12, md: 8 }}>
                  <TextInput source="street" label="Rua" fullWidth />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextInput source="streetNumber" label="Número" fullWidth />
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={{ maxWidth: 800 }}>
                <Grid size={{ xs: 12, md: 3 }}>
                  <CEPInput source="cep" label="CEP" fullWidth />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextInput source="neighbourhood" label="Bairro" fullWidth />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextInput source="city" label="Cidade" fullWidth />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <StateInput source="state" label="Estado" fullWidth />
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={{ maxWidth: 800 }}>
                <Grid size={{ xs: 12, md: 3 }}>
                  <NumberInput source="latitude" label="Latitude" fullWidth />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <NumberInput source="longitude" label="Longitude" fullWidth />
                </Grid>
              </Grid>
            </FormTab>
          )
        })}
        {permissions && permissions.includes(PERMISSIONS.REGISTER_STATION_PAYMENT) && (
          <FormTab label='Pagamento'>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <ReferenceArrayInput
                  sort={{ field: 'name', order: 'ASC' }}
                  perPage={null}
                  source="appPaymentMethodIds"
                  reference="app-payment-methods"
                  fullWidth
                >
                  <SelectArrayInput
                    label="Método de pagamento"
                    optionText="name" />
                </ReferenceArrayInput>
                <SelectInput
                  source="paymentGateway"
                  label="Gateway de pagamento"
                  choices={PaymentGatewayType.paymentGatewayChoices}
                  fullWidth
                />
                <FormDataConsumer>
                  {({ formData: { paymentGateway } }) => {
                    switch (paymentGateway) {
                      case PaymentGatewayType.fixpay:
                        return <TextInput label="Token Fixpay" source="fixpayToken" fullWidth />;
                      case PaymentGatewayType.quality:
                        return (
                          <>
                            <TextInput style={{ display: "block" }} label="Conta da Quality" source="qualityAccount" fullWidth />
                            <TextInput style={{ display: "block" }} label="Chave da Quality" source="qualityKey" fullWidth />
                          </>
                        );
                      case PaymentGatewayType.conferePix:
                        return <TextInput label="Token Confere Pix" source="conferePixToken" fullWidth />;
                      case PaymentGatewayType.bancoDoBrasil:
                        return (
                          <TextInput style={{ display: "block" }} label="Chave PIX" source="bbPixKey" fullWidth helperText="As demais credenciais do BB são configuradas na Rede" />
                        );
                      default:
                        return <></>;
                    }
                  }}
                </FormDataConsumer>
              </Grid>
            </Grid>
          </FormTab>
        )}
        {Permissions({
          permissions: [PERMISSIONS.REGISTER_STATION_GPS, PERMISSIONS.UPDATE_STATION],
          children: (
            <FormTab label='GPS'>
              <CoordinatesMapInput />
            </FormTab>
          )
        })}
        {permissions && permissions.includes(PERMISSIONS.REGISTER_STATION_SYNCHONIZATION) && (
          <FormTab label="Sincronização">
            <SelectInput
              validate={[required()]}
              label="Sistema"
              source="syncType"
              choices={choices}
              style={{ width: '260px' }}
            />
            <FormDataConsumer>
              {({ formData: { syncType } }) =>
                syncType === 'auto-system' && (
                  <>
                    <TextInput
                      source="credentials.host"
                      label="Host ou ip"
                      InputProps={{ autoComplete: 'off' }}
                      sx={{ display: 'block' }}
                    />
                    <NumberInput
                      source="credentials.port"
                      label="Porta"
                      InputProps={{ autoComplete: 'off' }}
                      sx={{ display: 'block' }}
                    />
                    <TextInput
                      source="credentials.user"
                      label="Usuário"
                      InputProps={{ autoComplete: 'nope' }}
                      sx={{ display: 'block' }}
                    />
                    <TextInput
                      source="credentials.password"
                      label="Senha"
                      type="password"
                      InputProps={{ autoComplete: 'new-password' }}
                      sx={{ display: 'block' }}
                    />
                    <TextInput
                      source="credentials.database"
                      label="Banco"
                      InputProps={{ autoComplete: 'off' }}
                      sx={{ display: 'block' }}
                    />
                  </>
                )
              }
            </FormDataConsumer>
            {permissions && permissions.includes(PERMISSIONS.ADMIN) && (
              <FormDataConsumer>
                {({ formData: { syncType } }) =>
                  syncType === 'auto-system' &&
                  <TextInput
                    label="Último Ponto da Sincronização"
                    source="lastInsertedTransaction"
                    style={{ width: '260px' }}
                  />
                }
              </FormDataConsumer>
            )}
          </FormTab>)}
        {Permissions({
          permissions: [PERMISSIONS.REGISTER_STATION_OPENING_HOURS, PERMISSIONS.UPDATE_STATION],
          children: (
            <FormTab label="Horários">
              <FormDataConsumer>
                {({ formData }) => {
                  if (formData.openingHours && formData.openingHours.length !== 7) {
                    const registeredDays = formData.openingHours.map(record => record.dayOfTheWeek);
                    const notRegisteredDays = [...daysOfTheWeek].filter(wd => !registeredDays.includes(wd.id));
                    const openingHours = [...formData.openingHours];
                    notRegisteredDays.forEach(nrd => {
                      openingHours.push({ dayOfTheWeek: nrd.id, closed: true });
                    });
                    formData.openingHours = openingHours.sort((a, b) => {
                      if (a.dayOfTheWeek > b.dayOfTheWeek) {
                        return 1;
                      } else if (a.dayOfTheWeek < b.dayOfTheWeek) {
                        return -1;
                      }
                      return 0;
                    });
                  }
                  return (
                    <ArrayInput source="openingHours" label="">
                      <SimpleFormIterator disableAdd disableRemove disableReordering>
                        <FormDataConsumer>
                          {({ scopedFormData }) => {
                            const day = daysOfTheWeek.find(wd => wd.id === scopedFormData.dayOfTheWeek).name;
                            return (
                              <p style={{ fontSize: 16, margin: '20px 0' }}>{day}</p>
                            );
                          }}
                        </FormDataConsumer>
                        <FormDataConsumer>
                          {({ scopedFormData }) => {
                            return (
                              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', margin: '0 0 20px 0' }}>
                                <BooleanInput
                                  source="closed"
                                  label='Fechado'
                                  sx={{ mr: 2.5 }}
                                />
                                {scopedFormData && !scopedFormData.closed &&
                                  <BooleanInput
                                    source="open24hrs"
                                    label='Aberto 24 horas'
                                    sx={{ mr: 2.5 }}
                                  />
                                }
                                {scopedFormData && !scopedFormData.closed && !scopedFormData.open24hrs &&
                                  <Fragment>
                                    <TimeInput
                                      record={scopedFormData}
                                      variant='filled'
                                      label='Hora de abertura'
                                      source="startTime"
                                      style={{ margin: '0 20px 0 0' }}
                                      fullDate
                                    />
                                    <TimeInput
                                      record={scopedFormData}
                                      variant='filled'
                                      label='Hora de fechamento'
                                      source="endTime"
                                      fullDate
                                    />
                                  </Fragment>
                                }
                              </Box>
                            );
                          }}
                        </FormDataConsumer>
                      </SimpleFormIterator>
                    </ArrayInput>
                  );
                }}
              </FormDataConsumer>
            </FormTab>
          )
        })}
        {Permissions({
          permissions: [PERMISSIONS.UPDATE_STATION],
          children: (
            <FormTab label="Feriados">
              <ArrayInput source="stationHolidays" label="">
                <SimpleFormIterator disableReordering>
                  <DateInput source="date" label="Data" />
                </SimpleFormIterator>
              </ArrayInput>
            </FormTab>
          )
        })}
        <Permission permission={PERMISSIONS.REGISTER_STATION_FINANCIAL}>
          <FormTab label="Financeiro">
            <FinancesStation />
          </FormTab>
        </Permission>
        <Permission permission={PERMISSIONS.REGISTER_STATION_GESTAO_CLICK}>
          <FormTab label="Gestão Click">
            <GestaoClickStation />
          </FormTab>
        </Permission>
        <CheckChainUsesModule module={ModuleKey.DOTZ}>
          <FormTab label="Dotz">
            <TextInput source="dotzUser" label="Usuário" sx={{ minWidth: 300 }} />
            <PasswordInput source="dotzPassword" label="Senha" inputProps={{ autocomplete: 'current-password' }} sx={{ minWidth: 300 }} />
            <PasswordInput source="dotzCertPassphrase" label="Senha do certificado" inputProps={{ autocomplete: 'current-password' }} sx={{ minWidth: 300 }} />
            <FileInput source="dotzCertificate" label="Certificado Dotz" accept={{ 'application/x-pkcs12': ['.pfx'] }}>
              <FileField source="src" title="title" />
            </FileInput>
          </FormTab>
        </CheckChainUsesModule>
        <Permission permission={PERMISSIONS.ADMIN}>
          <FormTab label="Integrações">
            <Grid container spacing={2} sx={{ maxWidth: 800 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormDataConsumer>
                  {({ formData }) => {
                    return (
                      <>
                        <Tooltip sxContainer={{ alignItems: 'flex-start' }} sxTooltipContainer={{ paddingTop: 5 }} placement="top-start" title={translate('tooltips.useSanvitronIntegration')}>
                          <BooleanInput
                            label="Integrar com Sanvitron"
                            source="useSanvitronIntegration"
                          />
                        </Tooltip>
                        {formData.useSanvitronIntegration && (
                          <>
                            <TextInput
                              source="sanvitronLogin"
                              label="Usuário Sanvitron"
                              fullWidth
                            />
                            <PasswordInput
                              source="sanvitronPassword"
                              label="Senha Sanvitron"
                              fullWidth
                            />
                            <TextInput
                              source="sanvitronBaseUrl"
                              label="URL Sanvitron"
                              fullWidth
                            />
                          </>
                        )}
                      </>
                    );
                  }}
                </FormDataConsumer>
                <Divider fullWidth />
              </Grid>
            </Grid>
          </FormTab>
        </Permission>
        <Permission permission={PERMISSIONS.ADMIN}>
          <FormTab label="Histórico">
            <EntityChangeLogTab entityType="place" />
          </FormTab>
        </Permission>
      </TabbedForm>
    </Edit>
  );
};

export default EditStation;
